import { Component, OnInit } from '@angular/core';
import { faCheck, faCircleNotch, faFileLines, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Observable, concatMap, from, tap } from 'rxjs';
import { AdminService } from '../../api/services';
import { ToastService } from '../../services/toast.service';

interface ReportDataStep {
  label: string;
  run: () => Observable<unknown>;
}

interface ReportDataStatus {
  label: string;
  state: 'generating' | 'finished' | 'failed';
}

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
})
export class AdminComponent implements OnInit {
  faCheck = faCheck;
  faCircleNotch = faCircleNotch;
  faFileLines = faFileLines;
  faTriangleExclamation = faTriangleExclamation;

  isGeneratingReportData: boolean = false;
  reportDataStatuses: ReportDataStatus[] = [];

  constructor(
    private adminService: AdminService,
    private toastService: ToastService
  ) {}

  ngOnInit() {}

  generateReportData() {
    if (this.isGeneratingReportData === true) {
      // Avoid concurrent duplicate http requests if already running.
      return;
    }
    this.isGeneratingReportData = true;
    this.reportDataStatuses = [];

    // Each endpoint runs to completion before the next one starts.
    const steps: ReportDataStep[] = [
      { label: '60 Day Report', run: () => this.adminService.generate60DayData$Response() },
      { label: '180 Day Report', run: () => this.adminService.generate180DayData$Response() },
      { label: '1 Year Report', run: () => this.adminService.generate1YearData$Response() },
      { label: 'Final Report', run: () => this.adminService.generateFinalData$Response() },
    ];

    from(steps)
      .pipe(
        concatMap((step) => {
          const status: ReportDataStatus = { label: step.label, state: 'generating' };
          this.reportDataStatuses.push(status);
          return step.run().pipe(tap(() => (status.state = 'finished')));
        })
      )
      .subscribe({
        complete: () => {
          this.isGeneratingReportData = false;
          this.toastService.showToast('success', 'Report data generated');
        },
        error: (e) => {
          this.isGeneratingReportData = false;
          const current = this.reportDataStatuses[this.reportDataStatuses.length - 1];
          if (current) {
            current.state = 'failed';
          }
          console.error('Error generating report data:', e);
          this.toastService.showToast('danger', `Problem generating ${current ? current.label : 'report data'}`);
        },
      });
  }

  reportDataStatusText(status: ReportDataStatus): string {
    switch (status.state) {
      case 'finished':
        return `Finished generating ${status.label}`;
      case 'failed':
        return `Problem generating ${status.label}`;
      default:
        return `Generating ${status.label}...`;
    }
  }
}
